import Plan from 'buy/Plan';
import User from 'data/User';
import * as React from 'react';
import { getStoreState, storeDispatch } from 'state/Store';
import { isBrowserFullySupported } from 'utils';
import {
  isAppSumoCodeValid,
  loadUser,
  useAppSumoCode
} from 'utils/FirebaseUtils';

import ComponentWithUser from '../components/ComponentWithUser';
import { Overlay } from '../components/Overlay';
import * as Actions from '../state/actions';
import firebase from 'firebase/app';
import 'firebase/database';

enum ViewState {
  Loading = 'Loading...',
  HasAlredyPlan = 'Already subscribed',
  InvalidCode = 'Invalid Code',
  Ready = 'Ready',
  Creating = 'Creating account...',
  AccountAlreadyExists = 'Account exists already',
  Upgrading = 'Upgrading...',
  Success = 'Success'
}

interface State {
  loggedUser: User | undefined;
  viewState: ViewState;
  validCode: string | undefined;
  userName: string;
  userEmail: string;
  userPassword: string;
}

export class AppSumo extends ComponentWithUser {
  state: State = {
    loggedUser: undefined,
    viewState: ViewState.Loading,
    validCode: undefined,
    userName: '',
    userEmail: '',
    userPassword: ''
  };

  async componentDidMount() {
    const { match } = this.props as any;
    const { id } = match.params;
    if (id) {
      const valid = await isAppSumoCodeValid(id);
      if (valid) {
        this.setState(
          {
            viewState: ViewState.Ready,
            validCode: id
          },
          () => {
            this.upgradeIfPossible();
          }
        );
        return;
      }
    }
    this.setState({
      viewState: ViewState.InvalidCode
    });
  }

  upgradeIfPossible = async () => {
    if (!this.state.loggedUser || !this.state.validCode) {
      return;
    }
    // if user is logged and has a subscription, tell to cancel subsription first
    // if user is logged and has not subscription, apply code automatically
    const { uid } = this.state.loggedUser;
    const user = await loadUser(uid);
    const plan = user.plan;
    if (plan === Plan.ProMonthly || plan === Plan.ProYearly) {
      this.setState({ viewState: ViewState.HasAlredyPlan });
    } else {
      // upgrade
      this.setState({ viewState: ViewState.Upgrading });
      user.plan = Plan.ProLifeAppsumo;
      const ref = firebase.database().ref(`users/${uid}`);
      await ref.update({
        plan: user.plan
      });
      storeDispatch(Actions.updateUser(user));
      await useAppSumoCode(this.state.validCode, uid, user.email);
      this.setState({ viewState: ViewState.Success });
    }
  };

  onUserReady(user: User) {
    const { viewState } = this.state;
    if (viewState === ViewState.Loading || viewState === ViewState.Ready) {
      this.setState({ loggedUser: user }, () => {
        this.upgradeIfPossible();
      });
    }
  }

  handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    this.setState({ viewState: ViewState.Creating });
    if (firebase) {
      const email = this.state.userEmail.trim();
      const password = this.state.userPassword.trim();
      try {
        const credentials = await firebase
          .auth()
          .createUserWithEmailAndPassword(email, password);
        // console.log(credentials);
        const user: any = credentials.user;
        const uid = user?.uid;
        if (!uid) {
          throw new Error('Something went wrong!');
        } else {
          user.name = this.state.userName.trim();
          user.plan = Plan.ProLifeAppsumo;
          const ref = firebase.database().ref(`users/${uid}`);
          await ref.update({
            email,
            name: user.name,
            plan: user.plan
          });
          storeDispatch(Actions.updateUser(user));
        }
        await useAppSumoCode(this.state.validCode!, uid, email);
        this.setState({ viewState: ViewState.Success });
      } catch (error) {
        var errorCode = error.code;
        if (errorCode === 'auth/email-already-in-use') {
          this.setState({ viewState: ViewState.AccountAlreadyExists });
          return;
        }
        console.error(error);
        alert(error.message);
        this.setState({ viewState: ViewState.Ready });
      }
    }
  };

  nameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userName: event.target.value });
  };

  emailChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userEmail: event.target.value });
  };

  passwordChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ userPassword: event.target.value });
  };

  exit = () => {
    getStoreState().app.history.replace('/');
    if (!isBrowserFullySupported()) {
      storeDispatch(Actions.showPartialSupportedBrowser());
    }
  };

  render() {
    const { viewState } = this.state;
    switch (viewState) {
      case ViewState.HasAlredyPlan:
        return (
          <Overlay title={ViewState.HasAlredyPlan}>
            <p>
              You already have a subscription to Beatflyer. Please cancel it and
              then visit this page again.
            </p>
          </Overlay>
        );
      case ViewState.InvalidCode:
        return (
          <Overlay title={ViewState.InvalidCode}>
            <p>
              The code provided is either invalid or already used. Feel free to
              contact <a href="mailto:info@beatflyer.com">info@beatflyer.com</a>{' '}
              if you believe otherwise.
            </p>
            <button
              onClick={this.exit}
              className="large primary"
              style={{ marginTop: 20 }}
            >
              Try the app anyway
            </button>
          </Overlay>
        );
      case ViewState.Ready:
      case ViewState.Creating: {
        const inProgress = viewState === ViewState.Creating;
        return (
          <Overlay title="Hello Sumo-ling!">
            <div className="content">
              <div className="form-code">
                <p>
                  Create an account on Beatflyer to enjoy your{' '}
                  <strong>lifetime access</strong>!
                </p>
                <p>
                  We are excited to see you here and look forward to assisting
                  you with any questions.
                </p>
                <label>
                  Full name
                  <input
                    type="text"
                    value={this.state.userName}
                    onChange={this.nameChanged}
                  />
                </label>
                <label>
                  Email
                  <input
                    type="text"
                    value={this.state.userEmail}
                    onChange={this.emailChanged}
                  />
                </label>
                <label>
                  Password
                  <input
                    type="password"
                    value={this.state.userPassword}
                    onChange={this.passwordChanged}
                  />
                </label>
                <button
                  onClick={this.handleSubmit}
                  className="button primary large"
                  disabled={
                    inProgress ||
                    this.state.userName.trim().length === 0 ||
                    this.state.userEmail.trim().length === 0 ||
                    this.state.userPassword.trim().length === 0
                  }
                >
                  {inProgress ? 'Creating...' : 'Create account'}
                </button>
              </div>
            </div>
          </Overlay>
        );
      }
      case ViewState.AccountAlreadyExists:
        return (
          <Overlay title={ViewState.AccountAlreadyExists}>
            <p>
              An account with this email exists already. Please sign in and then
              visit this page again.
            </p>
          </Overlay>
        );
      case ViewState.Success:
        return (
          <Overlay title="Account ready">
            <p>Your account is now ready and has all the features activated.</p>
            <button
              onClick={this.exit}
              className="large primary"
              style={{ marginTop: 20 }}
            >
              Let's get started
            </button>
          </Overlay>
        );
      default:
        return <Overlay title={viewState} />;
    }
  }
}



// WEBPACK FOOTER //
// ./src/modals/AppSumo.tsx